import React, { useState } from 'react';
import { getPaletteEntry } from './statusPalette';
import { ConsultPeersSubCard } from './ConsultPeersSubCard';
import { CreateArtifactsResult } from './CreateArtifactsResult';

// Task 28 — Generic tool-call pill with collapsed/expanded anatomy.
//
// Collapsed: `▸ [icon] tool_name  (234ms · ok)`
// Expanded adds arguments + result blocks beneath.
//
// Specialized tools (`consult_peers`, `create_artifacts`) delegate
// their result block to a child renderer that understands the richer
// payload. Everything else shows a truncated JSON preview.

export interface ToolCallEntry {
  identity?: {
    tool_name?: string;
    tool_call_id?: string;
    tool_category?: string;
  };
  // admin/work legacy flat shape
  tool_name?: string;
  tool_call_id?: string;
  function?: { name?: string };

  timing?: {
    duration_ms?: number;
    started_at?: string;
    ended_at?: string;
    llm_round?: number;
  };
  duration_ms?: number;

  outcome?: {
    status?: string;
    error_type?: string | null;
    error_message?: string | null;
  };

  arguments?: any;
  result?: any;
}

interface Props {
  call: ToolCallEntry;
  onArtifactSelect?: (artifactId: string) => void;
  // Surface-kind contract: when true, the pill renders header-only
  // and never expands in place. Used by surfaces like the Chat DAG
  // where the parent layout (dagre) reserves fixed dimensions and
  // in-place expansion would overlap neighbouring nodes. The
  // expanded view is reached via a sibling affordance on those
  // surfaces (e.g. double-click → Panel focus).
  forceCollapsed?: boolean;
}

function formatDuration(ms: number | undefined): string | null {
  if (ms == null) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function extractToolName(call: ToolCallEntry): string {
  return call.identity?.tool_name ?? call.tool_name ?? call.function?.name ?? '';
}

function extractStatus(call: ToolCallEntry): string {
  return call.outcome?.status ?? 'ok';
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max) + `… (${text.length - max} more chars)`;
}

function prettyJson(value: any, max: number): string {
  try {
    return truncate(JSON.stringify(value, null, 2), max);
  } catch {
    return String(value ?? '');
  }
}

// Consult-peers-specific summary for the pill header — "3/5 ok · 2 offline".
function consultPeersSummary(result: any): string {
  if (!result) return '';
  const r = result.result ?? result;
  const ok = r.ok_count;
  const total = r.peer_count;
  const offline = r.offline_count;
  const parts: string[] = [];
  if (typeof ok === 'number' && typeof total === 'number') {
    parts.push(`${ok}/${total} ok`);
  }
  if (typeof offline === 'number' && offline > 0) {
    parts.push(`${offline} offline`);
  }
  return parts.join(' · ');
}

// create_artifacts-specific summary — "7 created · 3 rate-limited".
function createArtifactsSummary(result: any): string {
  if (!result) return '';
  const r = result.result ?? result;
  const created = (r.created ?? []).length;
  const rateLimited = (r.rate_limited ?? []).length;
  const errors = (r.errors ?? []).length;
  const parts: string[] = [];
  if (created > 0) parts.push(`${created} created`);
  if (rateLimited > 0) parts.push(`${rateLimited} rate-limited`);
  if (errors > 0) parts.push(`${errors} error${errors === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

// Generic fallback for any tool whose result follows the canonical
// `{success: true|false, <noun>: [...]}` shape (which most query /
// list tools do — search, roster, tool_docs, attention_items,
// suggestions_read, wins_read, event_timeline_query, skills_query,
// cross_system_query, etc.). Tools that don't fit produce '' and the
// pill header just shows duration + status, no harm.
function genericResultSummary(result: any): string {
  if (!result) return '';
  const r = result.result ?? result;
  if (typeof r !== 'object' || r == null) return '';

  // Explicit-failure: surface the reason in the header.
  if (r.success === false) {
    const err = typeof r.error === 'string' ? r.error : '';
    if (err) {
      const trimmed = err.length > 60 ? `${err.slice(0, 60)}…` : err;
      return `✗ ${trimmed}`;
    }
    return '✗ failed';
  }

  // Explicit count fields take precedence (some tools surface them).
  if (typeof r.count === 'number') return `${r.count}`;
  if (typeof r.total === 'number') return `${r.total}`;

  // First array-valued top-level key is usually the main payload.
  // Skip diagnostic arrays so we never lead with "0 errors" / "0 warnings".
  const SKIP = new Set(['errors', 'warnings', 'candidates', 'phase_trail']);
  for (const [k, v] of Object.entries(r)) {
    if (Array.isArray(v) && !SKIP.has(k)) {
      return `${v.length} ${k}`;
    }
  }

  // Document-style tools: surface the action that was taken.
  if (typeof r.action === 'string') return r.action;

  return '';
}

export const ToolCallPill: React.FC<Props> = ({ call, onArtifactSelect, forceCollapsed }) => {
  const [expandedState, setExpanded] = useState(false);
  // Surfaces that can't accommodate the expanded block (the Chat DAG)
  // pass forceCollapsed — any click still feels like a click (the
  // chevron hides entirely) but the expand never fires.
  const expanded = forceCollapsed ? false : expandedState;
  const toolName = extractToolName(call);
  const status = extractStatus(call);
  const palette = getPaletteEntry(status);
  const duration = formatDuration(call.timing?.duration_ms ?? call.duration_ms);
  const labelName = toolName.replace(/^admin_/, '');

  let headerSummary = '';
  if (toolName === 'consult_peers') {
    headerSummary = consultPeersSummary(call.result);
  } else if (toolName === 'create_artifacts') {
    headerSummary = createArtifactsSummary(call.result);
  } else {
    // Generic fallback covers any tool whose result follows the
    // canonical {success, <noun>: [...]} shape — search / roster /
    // tool_docs / attention_items / etc. Tools without a fitting
    // shape return '' and the pill header just stays meta-only.
    headerSummary = genericResultSummary(call.result);
  }

  // Compose the parenthetical metadata in one pass. Status label is
  // elided on `ok` (spec § 3.2). Example outputs:
  //   "(234ms)"
  //   "(3.2s · 3/5 ok)"
  //   "(1.1s · timeout)"
  //   "(rate limited)"
  const metaParts: string[] = [];
  if (duration) metaParts.push(duration);
  if (status !== 'ok' && palette.label) metaParts.push(palette.label);
  if (headerSummary) metaParts.push(headerSummary);
  const metaText = metaParts.length > 0 ? `(${metaParts.join(' · ')})` : '';

  // Unwrap ToolResult-wrapped results so renderers see the inner dict.
  const resultInner = (call.result && typeof call.result === 'object' && 'result' in call.result)
    ? (call.result as any).result
    : call.result;

  const errorMsg = call.outcome?.error_message ?? null;

  return (
    <div
      className={`inline-flex flex-col rounded-md border border-transparent ${palette.bgClass} px-2 py-1 text-[11px] font-mono max-w-full`}
    >
      <button
        type="button"
        onClick={() => !forceCollapsed && setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-left"
        aria-expanded={expanded}
        disabled={forceCollapsed}
      >
        {!forceCollapsed && <span className="opacity-60">{expanded ? '▾' : '▸'}</span>}
        <span className={palette.color}>{palette.icon}</span>
        <span className="font-semibold">{labelName}</span>
        {metaText && <span className="opacity-70">{metaText}</span>}
        {errorMsg && status !== 'ok' && (
          <span className={`${palette.color} opacity-80 truncate max-w-[18rem]`}>— {errorMsg}</span>
        )}
      </button>

      {expanded && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {call.arguments && Object.keys(call.arguments).length > 0 && (
            <div>
              <div className="opacity-60 text-[10px] uppercase tracking-wide">args</div>
              <pre className="mt-0.5 whitespace-pre-wrap text-[11px] leading-snug opacity-90">
                {prettyJson(call.arguments, 600)}
              </pre>
            </div>
          )}

          {toolName === 'consult_peers' && resultInner && (
            <div>
              <div className="opacity-60 text-[10px] uppercase tracking-wide">peers</div>
              <ConsultPeersSubCard result={resultInner} />
            </div>
          )}

          {toolName === 'create_artifacts' && resultInner && (
            <div>
              <div className="opacity-60 text-[10px] uppercase tracking-wide">artifacts</div>
              <CreateArtifactsResult result={resultInner} onArtifactSelect={onArtifactSelect} />
            </div>
          )}

          {toolName !== 'consult_peers' && toolName !== 'create_artifacts' && resultInner != null && (
            <div>
              <div className="opacity-60 text-[10px] uppercase tracking-wide">result</div>
              <pre className="mt-0.5 whitespace-pre-wrap text-[11px] leading-snug opacity-90">
                {typeof resultInner === 'string'
                  ? truncate(resultInner, 800)
                  : prettyJson(resultInner, 800)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolCallPill;
